import { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useTapPeople } from '../projects/hooks';
import { SectionRow, SectionRows } from '../../components/ui/SectionRow';
import {
  attribution,
  buildResourceTree,
  categoryNameProblem,
  deleteWarning,
  hostOf,
  looseLinks,
  matchesQuery,
  platformOf,
  UNCATEGORISED_NAME,
} from './resourceLinks';
import {
  resourceErrorMessage,
  useClearSeededCategories,
  useCreateResourceCategory,
  useCreateResourceLink,
  useDeleteResourceCategory,
  useDeleteResourceLink,
  useResourceCategories,
  useResourceLinks,
  useUpdateResourceCategory,
} from './resourceQueries';
import type { ResourceCategory, ResourceLink, ResourceNode } from './resourceLinks';

/**
 * The Resource Directory: the link tree that made /files worth landing on.
 *
 * Every card carries who added it and when, because a directory without
 * attribution becomes a graveyard nobody feels able to prune — you cannot ask
 * about a link when you do not know whose it is.
 *
 * Platform badges are derived from the URL (resourceLinks.platformOf), never
 * stored, so a corrected link fixes its own badge.
 */

/**
 * Nesting depth as a CSS variable rather than a computed padding.
 *
 * Same contract as the notes tree: the component publishes how deep a row is
 * and the stylesheet decides what a level is worth. An inline
 * `calc(var(--space-3) * n)` would also be resolved by a real browser and NOT
 * by jsdom, which silently drops the declaration and takes the rest of the
 * rule with it.
 */
function depthStyle(depth: number): React.CSSProperties {
  return { '--section-depth': depth } as React.CSSProperties;
}

export function ResourceDirectory() {
  const categories = useResourceCategories();
  const links = useResourceLinks();
  const people = useTapPeople(true);
  const { access } = useAuth();
  const isAdmin =
    access.status === 'approved' &&
    (access.profile.internal_role === 'tap_admin' || access.profile.is_owner);

  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [creatingUnder, setCreatingUnder] = useState<string | null | undefined>(undefined);

  const createCategory = useCreateResourceCategory();
  const clearSeed = useClearSeededCategories();

  if (categories.isPending || links.isPending) {
    return <Skeleton lines={4} label="Loading resources" />;
  }
  if (categories.isError || links.isError) {
    return <InlineError>The resource directory could not be loaded.</InlineError>;
  }

  const allCategories = categories.data ?? [];
  const allLinks = links.data ?? [];
  const nameById = new Map(
    (people.data ?? []).map((person) => [
      person.id,
      person.display_name === '' ? person.email : person.display_name,
    ]),
  );

  const searching = query.trim() !== '';
  const visibleLinks = searching ? allLinks.filter((link) => matchesQuery(link, query)) : allLinks;
  const tree = buildResourceTree(allCategories, visibleLinks);
  const loose = looseLinks(allCategories, visibleLinks);

  function toggle(id: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function addSection(name: string, parentId: string | null) {
    const problem = categoryNameProblem(name, allCategories, parentId);
    if (problem !== null) {
      setError(problem);
      return;
    }
    setError('');
    createCategory.mutate(
      {
        name,
        colorKey: 'neutral',
        // Appended rather than inserted: a new section belongs at the end
        // until somebody moves it, and guessing a position reorders a page
        // people have learned the shape of.
        position: (allCategories.length + 1) * 100,
        parentId,
      },
      {
        onSuccess: () => setCreatingUnder(undefined),
        onError: (cause: Error) => setError(resourceErrorMessage(cause.message)),
      },
    );
  }

  const view = { collapsed, creatingUnder, searching };
  const actions = {
    toggle,
    addSub: (id: string) => setCreatingUnder(id),
    cancelCreate: () => setCreatingUnder(undefined),
    create: addSection,
    onError: setError,
  };

  return (
    <div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {note === '' ? null : <p className="resource-note text-body-meta">{note}</p>}

      <SectionRows>
        <SectionRow
          title="Directory"
          subtitle={`${String(allLinks.length)} link${allLinks.length === 1 ? '' : 's'} across ${String(allCategories.length)} section${allCategories.length === 1 ? '' : 's'}.`}
          action={
            <div className="resource-actions">
              <Button size="sm" onClick={() => setAdding(true)}>
                Add link
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setCreatingUnder(null)}>
                + Section
              </Button>
              {/* Admin only, and it says what it will do rather than just
                  doing it. The five demo sections are the first thing on this
                  page and the last thing anybody wants to delete one at a
                  time — but a button that quietly removes categories would be
                  the wrong kind of convenient. */}
              {!isAdmin ? null : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={clearSeed.isPending}
                  onClick={() => {
                    setError('');
                    clearSeed.mutate(undefined, {
                      onSuccess: (removed) =>
                        setNote(
                          removed === 0
                            ? 'Nothing to clear — the demo sections are gone or in use.'
                            : `Removed ${String(removed)} unused demo section${removed === 1 ? '' : 's'}. Anything you had filed is untouched.`,
                        ),
                      onError: (cause: Error) => setError(resourceErrorMessage(cause.message)),
                    });
                  }}
                >
                  {clearSeed.isPending ? 'Clearing…' : 'Clear demo sections'}
                </Button>
              )}
            </div>
          }
        >
          <Input
            label="Search resources"
            labelHidden
            placeholder="Search links, platforms, sections…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {creatingUnder !== null ? null : (
            <NewSectionRow
              depth={0}
              onCancel={() => setCreatingUnder(undefined)}
              onCreate={(name) => addSection(name, null)}
            />
          )}
        </SectionRow>

        {allLinks.length === 0 && allCategories.length === 0 ? (
          <SectionRow title="Empty" subtitle="Nothing has been filed yet.">
            <EmptyState
              title="No resources yet"
              description="Add the links the team opens every day — Canva, trackers, SOPs, dashboards."
            />
          </SectionRow>
        ) : searching && tree.every((node) => node.totalLinks === 0) && loose.length === 0 ? (
          <SectionRow title="No matches" subtitle="Nothing matched that search.">
            <EmptyState
              title="Nothing matches that search"
              description="Try a platform or a keyword."
            />
          </SectionRow>
        ) : (
          <>
            {tree.map((node) => (
              <SectionBranch
                key={node.id}
                node={node}
                categories={allCategories}
                nameById={nameById}
                view={view}
                actions={actions}
              />
            ))}
            {loose.length === 0 ? null : (
              <SectionRow
                title={UNCATEGORISED_NAME}
                subtitle="Links whose section was deleted. Move or re-file them."
              >
                <ul className="resource-rows">
                  {loose.map((link) => (
                    <LinkCard key={link.id} link={link} nameById={nameById} onError={setError} />
                  ))}
                </ul>
              </SectionRow>
            )}
          </>
        )}
      </SectionRows>

      {adding ? <AddLinkDialog onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

interface BranchView {
  collapsed: ReadonlySet<string>;
  creatingUnder: string | null | undefined;
  searching: boolean;
}

interface BranchActions {
  toggle: (id: string) => void;
  addSub: (id: string) => void;
  cancelCreate: () => void;
  create: (name: string, parentId: string | null) => void;
  onError: (message: string) => void;
}

/**
 * One section and everything inside it.
 *
 * Recursive, and declared at module scope rather than inside ResourceDirectory
 * — a component defined in the parent's body gets a new identity on every
 * render, which remounts this whole subtree and takes the focus out of the
 * inline rename field on the first keystroke.
 *
 * Top-level sections are SectionRows so they sit on the same 25/75 grid as
 * every other page. Subsections are NOT: a nested SectionRow would put a
 * second 25% anchor column inside the first one's 75% canvas, and the two
 * grids would not line up with anything. They render as indented rows on the
 * canvas instead, which is what a subsection is.
 */
function SectionBranch({
  node,
  categories,
  nameById,
  view,
  actions,
}: {
  node: ResourceNode;
  categories: ResourceCategory[];
  nameById: Map<string, string>;
  view: BranchView;
  actions: BranchActions;
}) {
  // A search has already narrowed the list, so opening everything is what the
  // person asked for; a section they collapsed yesterday hiding today's only
  // match is the bug this avoids.
  const open = view.searching || !view.collapsed.has(node.id);

  const body = (
    <>
      {!open ? null : (
        <>
          {node.links.length === 0 ? null : (
            <ul className="resource-rows">
              {node.links.map((link) => (
                <LinkCard key={link.id} link={link} nameById={nameById} onError={actions.onError} />
              ))}
            </ul>
          )}
          {view.creatingUnder !== node.id ? null : (
            <NewSectionRow
              depth={node.depth + 1}
              onCancel={actions.cancelCreate}
              onCreate={(name) => actions.create(name, node.id)}
            />
          )}
          {node.children.map((child) => (
            <SubSection
              key={child.id}
              node={child}
              categories={categories}
              nameById={nameById}
              view={view}
              actions={actions}
            />
          ))}
        </>
      )}
    </>
  );

  return (
    <SectionRow
      title={
        <SectionHeading
          node={node}
          categories={categories}
          open={open}
          actions={actions}
          searching={view.searching}
        />
      }
      subtitle={`${String(node.totalLinks)} link${node.totalLinks === 1 ? '' : 's'}`}
    >
      {body}
    </SectionRow>
  );
}

function SubSection({
  node,
  categories,
  nameById,
  view,
  actions,
}: {
  node: ResourceNode;
  categories: ResourceCategory[];
  nameById: Map<string, string>;
  view: BranchView;
  actions: BranchActions;
}) {
  const open = view.searching || !view.collapsed.has(node.id);
  return (
    <div className="resource-subsection" style={depthStyle(node.depth)}>
      <div className="resource-subsection-head">
        <SectionHeading
          node={node}
          categories={categories}
          open={open}
          actions={actions}
          searching={view.searching}
        />
        <span className="resource-subsection-count">{node.totalLinks}</span>
      </div>
      {!open ? null : (
        <>
          {node.links.length === 0 ? null : (
            <ul className="resource-rows">
              {node.links.map((link) => (
                <LinkCard key={link.id} link={link} nameById={nameById} onError={actions.onError} />
              ))}
            </ul>
          )}
          {view.creatingUnder !== node.id ? null : (
            <NewSectionRow
              depth={node.depth + 1}
              onCancel={actions.cancelCreate}
              onCreate={(name) => actions.create(name, node.id)}
            />
          )}
          {node.children.map((child) => (
            <SubSection
              key={child.id}
              node={child}
              categories={categories}
              nameById={nameById}
              view={view}
              actions={actions}
            />
          ))}
        </>
      )}
    </div>
  );
}

/** The name, the chevron, and the three things you can do to a section. */
function SectionHeading({
  node,
  categories,
  open,
  actions,
  searching,
}: {
  node: ResourceNode;
  categories: ResourceCategory[];
  open: boolean;
  actions: BranchActions;
  searching: boolean;
}) {
  const rename = useUpdateResourceCategory();
  const remove = useDeleteResourceCategory();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.name);

  function commit() {
    const problem = categoryNameProblem(
      draft,
      categories,
      categories.find((entry) => entry.id === node.id)?.parent_id ?? null,
      node.id,
    );
    if (problem !== null) {
      actions.onError(problem);
      return;
    }
    actions.onError('');
    rename.mutate(
      { id: node.id, name: draft },
      {
        onSuccess: () => setEditing(false),
        onError: (cause: Error) => actions.onError(resourceErrorMessage(cause.message)),
      },
    );
  }

  if (editing) {
    return (
      <span className="resource-section-heading">
        <Input
          label={`Rename ${node.name}`}
          labelHidden
          value={draft}
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') {
              setDraft(node.name);
              setEditing(false);
            }
          }}
        />
        <Button size="sm" onClick={commit} disabled={rename.isPending}>
          Save
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDraft(node.name);
            setEditing(false);
          }}
        >
          Cancel
        </Button>
      </span>
    );
  }

  return (
    <span className="resource-section-heading">
      {/* Hidden while searching: the tree is force-opened, so a control
          claiming to collapse something that will not collapse is worse than
          no control. */}
      {searching ? null : (
        <button
          type="button"
          className="resource-section-twist"
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}
          onClick={() => actions.toggle(node.id)}
        >
          {open ? '▾' : '▸'}
        </button>
      )}
      <span className="resource-section-name">{node.name}</span>
      <span className="resource-section-tools">
        <button
          type="button"
          className="resource-section-action"
          aria-label={`Rename ${node.name}`}
          onClick={() => {
            setDraft(node.name);
            setEditing(true);
          }}
        >
          Rename
        </button>
        <button
          type="button"
          className="resource-section-action"
          aria-label={`New subsection inside ${node.name}`}
          onClick={() => actions.addSub(node.id)}
        >
          +
        </button>
        <button
          type="button"
          className="resource-section-action resource-section-action--danger"
          aria-label={`Delete ${node.name}`}
          disabled={remove.isPending}
          onClick={() => {
            // Said in full before it happens. Subsections cascade; links do
            // not — they fall to Uncategorised — and nobody should have to
            // know which is which from memory.
            if (!window.confirm(deleteWarning(node))) {
              return;
            }
            actions.onError('');
            remove.mutate(node.id, {
              onError: (cause: Error) => actions.onError(resourceErrorMessage(cause.message)),
            });
          }}
        >
          ×
        </button>
      </span>
    </span>
  );
}

/** The inline "name it and press Enter" row, used at every depth. */
function NewSectionRow({
  depth,
  onCreate,
  onCancel,
}: {
  depth: number;
  onCreate: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  return (
    <div className="resource-section-new" style={depthStyle(depth)}>
      <Input
        label="New section name"
        labelHidden
        placeholder="New section name…"
        value={name}
        autoFocus
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onCreate(name);
            setName('');
          }
          if (event.key === 'Escape') {
            onCancel();
          }
        }}
      />
      <Button size="sm" onClick={() => onCreate(name)}>
        Add
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

function LinkCard({
  link,
  nameById,
  onError,
}: {
  link: ResourceLink;
  nameById: Map<string, string>;
  onError: (message: string) => void;
}) {
  const { access } = useAuth();
  const remove = useDeleteResourceLink();
  const [confirming, setConfirming] = useState(false);

  const viewer = access.status === 'approved' ? access.profile : null;
  // Matches the policy: your own link, or a TAP admin tidying up. Showing the
  // control to anyone else would just produce a refusal they cannot act on.
  const mayRemove =
    viewer !== null && (link.created_by === viewer.id || viewer.internal_role === 'tap_admin');
  const platform = platformOf(link.url);

  return (
    <li className="resource-row">
      {/* The title carries the link and nothing else competes with it. A
          directory is scanned, not read: at a thousand links the only thing
          anybody looks for is the name, and every pixel spent on chrome around
          it is a row that did not fit on the screen. */}
      <a
        className="resource-row-title"
        href={link.url}
        target="_blank"
        rel="noreferrer"
        // The description survives as the hover text rather than a paragraph.
        // It is worth keeping and not worth a line of its own.
        title={link.description === '' ? undefined : link.description}
      >
        {link.title}
      </a>
      {link.is_pinned ? (
        <Badge tone="warning" micro>
          Pinned
        </Badge>
      ) : null}
      {/* Platform, host and attribution share one muted line. Attribution stays
          because a directory nobody can attribute is a directory nobody feels
          able to prune — but it does not need its own row to do that job. */}
      <span className="resource-row-meta">
        {platform.label} · {hostOf(link.url)} ·{' '}
        {attribution(nameById.get(link.created_by) ?? '', link.created_at)}
      </span>
      {mayRemove ? (
        <div className="resource-row-actions">
          {confirming ? (
            <>
              <Button
                variant="danger"
                size="sm"
                disabled={remove.isPending}
                onClick={() => {
                  onError('');
                  remove.mutate(link.id, {
                    onError: (cause) => onError(resourceErrorMessage(cause.message)),
                  });
                }}
              >
                Confirm remove
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${link.title}`}
              onClick={() => setConfirming(true)}
            >
              Remove
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
}

function AddLinkDialog({ onClose }: { onClose: () => void }) {
  const categories = useResourceCategories();
  const create = useCreateResourceLink();
  const createCategory = useCreateResourceCategory();
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [pinned, setPinned] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [error, setError] = useState('');

  const rows = categories.data ?? [];

  function submit() {
    if (title.trim() === '' || url.trim() === '') {
      setError('A link needs a name and a URL.');
      return;
    }
    setError('');
    create.mutate(
      {
        title,
        url,
        categoryId: categoryId === '' ? null : categoryId,
        description,
        isPinned: pinned,
      },
      {
        onSuccess: onClose,
        onError: (cause) => setError(resourceErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Dialog
      open
      title="Add a resource link"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Add link'}
          </Button>
        </>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <Input
        label="Name"
        placeholder="CG Order Tracker"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        label="URL"
        placeholder="https://…"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        hint={url.trim() === '' ? undefined : `Badge: ${platformOf(url).label}`}
      />
      <Select
        label="Category"
        value={categoryId}
        placeholder="Uncategorised"
        options={rows.map((category) => ({ value: category.id, label: category.name }))}
        onChange={(event) => setCategoryId(event.target.value)}
      />
      <div className="resource-new-category">
        <Input
          label="Or create a category"
          placeholder="SOPs & Guides"
          value={newCategory}
          onChange={(event) => setNewCategory(event.target.value)}
        />
        <Button
          variant="secondary"
          disabled={newCategory.trim() === '' || createCategory.isPending}
          onClick={() => {
            setError('');
            createCategory.mutate(
              {
                name: newCategory,
                colorKey: 'neutral',
                position: (rows[rows.length - 1]?.position ?? 0) + 1000,
              },
              {
                onSuccess: () => setNewCategory(''),
                onError: (cause) => setError(resourceErrorMessage(cause.message)),
              },
            );
          }}
        >
          Create
        </Button>
      </div>
      <Textarea
        label="Description (optional)"
        rows={2}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <label className="resource-pin">
        <input type="checkbox" checked={pinned} onChange={() => setPinned((value) => !value)} /> Pin
        to the top of its category
      </label>
    </Dialog>
  );
}
